// Small presentation helpers. Relative times are computed against the REAL clock —
// the store carries live PocketBase/`new Date()` timestamps now, so a fixed "now"
// made every fresh event read "just now" forever and misclassified credential age
// (H02). Negative diffs (a timestamp slightly ahead of this client) clamp to 0.

export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  const diff = Math.max(0, Date.now() - then)
  const sec = Math.round(diff / 1000)
  if (sec < 45) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 30) return `${day}d ago`
  const mo = Math.round(day / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.round(mo / 12)}y ago`
}

/** True when `iso` parses to a real date. Free accounts carry an empty
 *  `renewsOn` (no billing renewal), so callers guard on this before claiming a
 *  renewal/reset date rather than rendering "Invalid Date". */
export function isValidDate(iso: string): boolean {
  return !!iso && !Number.isNaN(Date.parse(iso))
}

export function shortDate(iso: string): string {
  if (!isValidDate(iso)) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function fullDate(iso: string): string {
  if (!isValidDate(iso)) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Deterministic two-stop gradient from a hue, for generated avatars. */
export function avatarGradient(hue: number): string {
  return `linear-gradient(135deg, hsl(${hue} 55% 58%) 0%, hsl(${(hue + 40) % 360} 60% 42%) 100%)`
}

export function pct(used: number, total: number): number {
  if (total <= 0) return 0
  return Math.min(100, Math.round((used / total) * 100))
}
